import React from 'react'
import { createRootRoute, createRoute, redirect, Outlet } from '@tanstack/react-router'
import { MainLayout } from '@/components/layout/main-layout'
import { LoginPage } from '@/pages/login'
import { DashboardPage } from '@/pages/dashboard'
import { MonitorManagePage } from '@/pages/monitor/manage'
import { MonitorDataPage } from '@/pages/monitor/data'
import { AnalysisPage } from '@/pages/analysis'
import { VolumePage } from '@/pages/volume'
import { PublicOptionListPage } from '@/pages/publicoption/list'
import { PublicOptionDetailPage } from '@/pages/publicoption/detail'
import { ReportListPage } from '@/pages/report/list'
import { ReportDetailPage } from '@/pages/report/detail'
import { WarningSettingsPage } from '@/pages/settings/warning'
import { WarningMessagePage } from '@/pages/settings/warning-msg'
import { PreferencePage } from '@/pages/settings/preference'
import { FeedbackPage } from '@/pages/settings/feedback'
import { UserManagePage } from '@/pages/settings/users'

// Auth check helper
function checkAuth() {
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true'
  const token = localStorage.getItem('auth_token')
  if (!isLoggedIn && !token) {
    throw redirect({ to: '/login' })
  }
}

// Root route - renders child routes via Outlet
const rootRoute = createRootRoute({
  component: () => React.createElement(Outlet, null),
  notFoundComponent: () =>
    React.createElement('div', { className: 'flex h-full items-center justify-center' },
      React.createElement('div', { className: 'text-center' },
        React.createElement('h1', { className: 'text-4xl font-bold text-muted-foreground' }, '404'),
        React.createElement('p', { className: 'mt-2 text-muted-foreground' }, 'Page Not Found')
      )
    ),
})

// Login route (standalone, no MainLayout)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

// Layout route for authenticated pages
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_layout',
  component: MainLayout,
  beforeLoad: checkAuth,
})

// Index route - redirect to login
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/login' })
  },
})

// Dashboard
const dashboardRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/dashboard',
  component: DashboardPage,
})

// Monitor routes
const monitorRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/monitor',
  component: MonitorManagePage,
})

const monitorDataRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/monitor/data',
  component: MonitorDataPage,
})

// Analysis route
const analysisRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/analysis',
  component: AnalysisPage,
})

// Volume route
const volumeRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/volume',
  component: VolumePage,
})

// Public Option routes
const publicOptionRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/publicoption',
  component: PublicOptionListPage,
})

const publicOptionDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/publicoption/$id',
  component: PublicOptionDetailPage,
})

// Report routes
const reportRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/report',
  component: ReportListPage,
})

const reportDetailRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/report/$id',
  component: ReportDetailPage,
})

// Settings routes
const warningRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/warning',
  component: WarningSettingsPage,
})

const warningMsgRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/warning-msg',
  component: WarningMessagePage,
})

const preferenceRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/preference',
  component: PreferencePage,
})

const userManageRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/users',
  component: UserManagePage,
})

const feedbackRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings/feedback',
  component: FeedbackPage,
})

// Build route tree
export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  layoutRoute.addChildren([
    dashboardRoute,
    monitorRoute,
    monitorDataRoute,
    analysisRoute,
    volumeRoute,
    publicOptionRoute,
    publicOptionDetailRoute,
    reportRoute,
    reportDetailRoute,
    warningRoute,
    warningMsgRoute,
    preferenceRoute,
    userManageRoute,
    feedbackRoute,
  ]),
])
